import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { userGroupApi } from '~/apis/userGroupApi'
import { useUserState } from '~/atoms/UserState'
import { toaster } from '~/components/ui/toaster'
import { ApplicationRoutes } from '~/types/RoutingTypes'
import { getUserRoleClassification } from '~/utils/auth'

import {
  useCreateUserGroup,
  UserGroupError,
  UserGroupForm,
} from '@mangovoice/user-groups-ui'
import type {
  UserGroupCreate,
  UserGroupUpdate,
} from '@mangovoice/user-groups-ui'

export const UserGroupAddPage = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { pbxId, userRoles } = useUserState({ canAutoRefresh: false })

  const parsedPbxId = pbxId ?? NaN
  const { isMangoStaff, isSuperuser } = getUserRoleClassification(userRoles)
  const canEditAccessFields = isMangoStaff || isSuperuser

  const createMutation = useCreateUserGroup({
    api: userGroupApi,
    pbxId: parsedPbxId,
  })

  const handleSubmit = (data: UserGroupCreate) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        toaster.create({
          title: t('userGroups.toast.createdTitle', 'User group created'),
          type: 'success',
          duration: 4000,
        })
        navigate(ApplicationRoutes.USER_GROUPS)
      },
      onError: (err: Error) => {
        if (err instanceof UserGroupError && err.code === 'forbidden') {
          toaster.create({
            title: t(
              'userGroups.toast.authRequiredTitle',
              'Authentication required',
            ),
            description: t(
              'userGroups.toast.authRequiredDescription',
              'Your session may have expired. Please re-authenticate and try again.',
            ),
            type: 'error',
            duration: 6000,
          })
        } else {
          toaster.create({
            title: t(
              'userGroups.toast.createFailedTitle',
              'Failed to create user group',
            ),
            description: err.message,
            type: 'error',
            duration: 5000,
          })
        }
      },
    })
  }

  if (!pbxId) {
    return null
  }

  return (
    <UserGroupForm
      api={userGroupApi}
      pbxId={parsedPbxId}
      mode="add"
      isSubmitting={createMutation.isPending}
      canEditAccessFields={canEditAccessFields}
      onSubmit={(data: UserGroupCreate | UserGroupUpdate) =>
        handleSubmit(data as UserGroupCreate)
      }
      onCancel={() => navigate(ApplicationRoutes.USER_GROUPS)}
      t={t}
      locale={i18n.language}
    />
  )
}
